import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { MoonLoader } from "react-spinners";
import { useState } from "react";

interface Enquiries {
  _id: string;
  name: string;
  phoneNumber: Number;
  email: string;
  subject: string;
  message: string;
  createdAt: Date;
}

interface TableProps {
  data: Enquiries[];
  loading?: boolean;
  onStatusChange?: (id: string, newStatus: string) => void;
}

export const EnquiryTable: React.FC<TableProps> = ({
  data,
  loading = false,
}) => {
  const [selectedMessage, setSelectedMessage] = useState<string>("");
  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const handleMessageClick = (message: string, subject: string) => {
    setSelectedMessage(message);
    setSelectedSubject(subject);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedMessage("");
    setSelectedSubject("");
  };

  const rows = data.map((enquiry: Enquiries, index: any) => {
    return {
      id: enquiry._id || index,
      name: enquiry.name,
      phoneNumber: enquiry.phoneNumber,
      email: enquiry.email,
      subject: enquiry.subject,
      message: enquiry.message,
      createdAt: new Date(enquiry.createdAt).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  });

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", width: 150 },
    { field: "phoneNumber", headerName: "Phone Number", width: 150 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "subject", headerName: "Subject", width: 200 },
    { field: "createdAt", headerName: "Submitted On", width: 200 },
    {
      field: "message",
      headerName: "Message",
      width: 400,
      renderCell: (params) => {
        const message = params.value as string;
        const truncatedMessage =
          message.length > 100 ? `${message.substring(0, 100)}...` : message;

        return (
          <Box
            sx={{
              cursor: "pointer",
            }}
            onClick={() => handleMessageClick(message, params.row.subject)}
          >
            {truncatedMessage}
          </Box>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <MoonLoader color="#1976d2" size={40} />
        <Typography sx={{ ml: 2 }}>Loading Enquiries...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: 550, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="standard"
          disableRowSelectionOnClick
          disableColumnFilter
          disableDensitySelector
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f8f9fa",
            },
          }}
        />
      </Box>

      {/* Message Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            {selectedSubject}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
            {selectedMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
